import React, { useState, useMemo, useEffect } from 'react';
import { ArrowRightLeft, Clock } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useI18n } from '@/i18n/index.jsx';
import { TIMEZONE_CAPITALS } from '@/utils/timezoneData.js';

// Retorna o deslocamento (em minutos a leste de UTC) de um fuso IANA
// em um instante UTC específico. Usa Intl.DateTimeFormat com formatToParts
// para respeitar as regras de horário de verão da base IANA do navegador.
function getOffsetMinutes(timeZone, instant) {
  try {
    const dtf = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false
    });
    const parts = dtf.formatToParts(instant);
    const g = (t) => parts.find(p => p.type === t)?.value;
    let hour = parseInt(g('hour'), 10);
    if (hour === 24) hour = 0; // normalização CLDR
    const wallMs = Date.UTC(
      parseInt(g('year'), 10),
      parseInt(g('month'), 10) - 1,
      parseInt(g('day'), 10),
      hour,
      parseInt(g('minute'), 10),
      parseInt(g('second'), 10)
    );
    return Math.round((wallMs - instant.getTime()) / 60000);
  } catch (e) {
    return 0;
  }
}

// Interpreta um horário de parede (y, mo, d, h, mi) em um fuso IANA e devolve
// o instante UTC correspondente. Resolve ambiguidades de DST por iteração.
function wallClockToUtc(timeZone, y, mo, d, h, mi) {
  const wallAsUtc = Date.UTC(y, mo - 1, d, h, mi);
  let instant = new Date(wallAsUtc);
  for (let i = 0; i < 4; i++) {
    const off = getOffsetMinutes(timeZone, instant);
    instant = new Date(wallAsUtc - off * 60000);
  }
  return instant;
}

function formatInTz(timeZone, instant, locale) {
  return new Intl.DateTimeFormat(locale, {
    timeZone,
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false
  }).format(instant);
}

// Data no formato YYYY-MM-DD conforme o fuso (para comparar dias do calendário).
function dateStrInTz(timeZone, instant) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone, year: 'numeric', month: '2-digit', day: '2-digit'
  }).format(instant);
}

function offsetLabel(minutes) {
  const sign = minutes >= 0 ? '+' : '−';
  const abs = Math.abs(minutes);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return `UTC${sign}${h}${m ? ':' + String(m).padStart(2, '0') : ''}`;
}

// Heurística de horário de verão: compara o deslocamento agora com o de
// 6 meses depois. Se diferirem e o atual for maior, o instante está em DST.
function isDST(timeZone, instant) {
  const off = getOffsetMinutes(timeZone, instant);
  const later = new Date(instant.getTime());
  later.setUTCMonth((later.getUTCMonth() + 6) % 12);
  const off2 = getOffsetMinutes(timeZone, later);
  return off !== off2 && off > off2;
}

function nowPartsInTz(timeZone) {
  const now = new Date();
  const date = new Intl.DateTimeFormat('en-CA', {
    timeZone, year: 'numeric', month: '2-digit', day: '2-digit'
  }).format(now);
  let time = new Intl.DateTimeFormat('en-GB', {
    timeZone, hour: '2-digit', minute: '2-digit', hour12: false
  }).format(now);
  if (time === '24:00') time = '00:00';
  return { date, time };
}

function TimezoneConverter({ initialFromUnit = null, initialToUnit = null }) {
  const { lang } = useI18n();
  const isEN = lang === 'en';
  const locale = isEN ? 'en-US' : 'pt-BR';

  const sortedCapitals = useMemo(() => {
    return [...TIMEZONE_CAPITALS].sort((a, b) => {
      const an = isEN ? a.countryEn : a.countryPt;
      const bn = isEN ? b.countryEn : b.countryPt;
      return an.localeCompare(bn, isEN ? 'en' : 'pt');
    });
  }, [isEN]);

  const valid = (id) => !!(id && TIMEZONE_CAPITALS.find(c => c.id === id));
  const defaultFrom = valid(initialFromUnit) ? initialFromUnit : 'brasil-brasilia';
  const defaultTo = valid(initialToUnit) ? initialToUnit : 'reino-unido-londres';

  const [fromId, setFromId] = useState(defaultFrom);
  const [toId, setToId] = useState(defaultTo);
  const [dateVal, setDateVal] = useState(() => nowPartsInTz(defaultFrom).date);
  const [timeVal, setTimeVal] = useState(() => nowPartsInTz(defaultFrom).time);

  useEffect(() => {
    if (valid(initialFromUnit)) setFromId(initialFromUnit);
    if (valid(initialToUnit)) setToId(initialToUnit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFromUnit, initialToUnit]);

  const fromCap = TIMEZONE_CAPITALS.find(c => c.id === fromId) || TIMEZONE_CAPITALS[0];
  const toCap = TIMEZONE_CAPITALS.find(c => c.id === toId) || TIMEZONE_CAPITALS[1];

  const result = useMemo(() => {
    if (!dateVal || !timeVal) return null;
    const [y, mo, d] = dateVal.split('-').map(Number);
    const [h, mi] = timeVal.split(':').map(Number);
    if (!y || isNaN(h)) return null;
    const instant = wallClockToUtc(fromCap.tz, y, mo, d, h, mi);
    const originOffset = getOffsetMinutes(fromCap.tz, instant);
    const destOffset = getOffsetMinutes(toCap.tz, instant);
    const diffMin = destOffset - originOffset;
    const originDateStr = dateStrInTz(fromCap.tz, instant);
    const destDateStr = dateStrInTz(toCap.tz, instant);
    const dayDiff = Math.round((Date.parse(destDateStr) - Date.parse(originDateStr)) / 86400000);
    return { instant, originOffset, destOffset, diffMin, dayDiff };
  }, [dateVal, timeVal, fromCap, toCap]);

  const useNow = () => {
    const p = nowPartsInTz(fromCap.tz);
    setDateVal(p.date);
    setTimeVal(p.time);
  };

  const swap = () => { setFromId(toId); setToId(fromId); };

  const capLabel = (c) => isEN ? `${c.countryEn} — ${c.capitalEn}` : `${c.countryPt} — ${c.capitalPt}`;

  const diffLabel = useMemo(() => {
    if (!result) return '';
    const min = result.diffMin;
    if (min === 0) return '';
    const sign = min > 0 ? '+' : '−';
    const abs = Math.abs(min);
    const h = Math.floor(abs / 60);
    const m = abs % 60;
    return m === 0 ? `${sign}${h} h` : `${sign}${h} h ${m} min`;
  }, [result]);

  const dayLabel = useMemo(() => {
    if (!result) return '';
    const n = result.dayDiff;
    if (n === 0) return isEN ? 'Same day' : 'Mesmo dia';
    if (n === 1) return isEN ? 'Next day (+1)' : 'Dia seguinte (+1)';
    if (n === -1) return isEN ? 'Previous day (−1)' : 'Dia anterior (−1)';
    return isEN ? `${n > 0 ? '+' : ''}${n} days` : `${n > 0 ? '+' : ''}${n} dias`;
  }, [result, isEN]);

  const fromName = isEN ? fromCap.capitalEn : fromCap.capitalPt;
  const toName = isEN ? toCap.capitalEn : toCap.capitalPt;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="tz-date">{isEN ? 'Date' : 'Data'}</Label>
          <Input id="tz-date" type="date" value={dateVal} onChange={(e) => setDateVal(e.target.value)} className="h-12 bg-background text-foreground" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="tz-time">{isEN ? 'Time' : 'Hora'}</Label>
          <Input id="tz-time" type="time" value={timeVal} onChange={(e) => setTimeVal(e.target.value)} className="h-12 bg-background text-foreground" />
        </div>
      </div>

      <div className="flex flex-col md:flex-row items-center gap-6">
        <div className="w-full flex-1 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="tz-from">{isEN ? 'Origin capital' : 'Capital de origem'}</Label>
            <Select value={fromId} onValueChange={setFromId}>
              <SelectTrigger id="tz-from" className="bg-background text-foreground h-12">
                <SelectValue placeholder={isEN ? 'Select origin' : 'Selecione a origem'} />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {sortedCapitals.map(c => (
                  <SelectItem key={c.id} value={c.id}>{capLabel(c)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {result && (
            <div className="rounded-xl border border-border/60 bg-muted/30 p-4 text-sm">
              <p className="font-semibold text-foreground">{fromName}</p>
              <p className="text-muted-foreground mt-1">{formatInTz(fromCap.tz, result.instant, locale)}</p>
              <p className="text-xs text-muted-foreground mt-2">
                {offsetLabel(result.originOffset)}
                {isDST(fromCap.tz, result.instant) && (
                  <span className="ml-2 inline-flex items-center rounded-md bg-primary/10 px-2 py-0.5 text-primary font-medium">DST</span>
                )}
              </p>
            </div>
          )}
        </div>

        <button
          onClick={swap}
          className="mt-6 p-4 rounded-full bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 flex-shrink-0 group"
          aria-label={isEN ? 'Swap capitals' : 'Inverter capitais'}
        >
          <ArrowRightLeft className="w-6 h-6 transition-transform group-hover:rotate-180" />
        </button>

        <div className="w-full flex-1 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="tz-to">{isEN ? 'Destination capital' : 'Capital de destino'}</Label>
            <Select value={toId} onValueChange={setToId}>
              <SelectTrigger id="tz-to" className="bg-background text-foreground h-12">
                <SelectValue placeholder={isEN ? 'Select destination' : 'Selecione o destino'} />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {sortedCapitals.map(c => (
                  <SelectItem key={c.id} value={c.id}>{capLabel(c)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {result && (
            <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 text-sm">
              <p className="font-semibold text-foreground">{toName}</p>
              <p className="text-foreground mt-1 font-medium">{formatInTz(toCap.tz, result.instant, locale)}</p>
              <p className="text-xs text-muted-foreground mt-2">
                {offsetLabel(result.destOffset)}
                {isDST(toCap.tz, result.instant) && (
                  <span className="ml-2 inline-flex items-center rounded-md bg-primary/10 px-2 py-0.5 text-primary font-medium">DST</span>
                )}
              </p>
            </div>
          )}
        </div>
      </div>

      {result ? (
        <div className="rounded-2xl border border-border bg-muted/20 p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">
              {result.diffMin === 0
                ? (isEN ? `${toName} and ${fromName} share the same time.` : `${toName} e ${fromName} têm o mesmo horário.`)
                : (isEN
                    ? `${toName} is ${diffLabel} ${result.diffMin > 0 ? 'ahead of' : 'behind'} ${fromName}.`
                    : `${toName} está ${diffLabel} ${result.diffMin > 0 ? 'à frente de' : 'atrás de'} ${fromName}.`)}
            </p>
            <p className="text-sm">
              <span className="text-muted-foreground">{isEN ? 'Calendar: ' : 'Calendário: '}</span>
              <span className="text-foreground font-semibold">{dayLabel}</span>
            </p>
          </div>
          <button
            onClick={useNow}
            className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-muted transition-colors flex-shrink-0"
          >
            <Clock className="w-4 h-4" /> {isEN ? 'Use now' : 'Usar agora'}
          </button>
        </div>
      ) : (
        <div className="rounded-2xl border border-border bg-muted/20 p-5 text-sm text-muted-foreground">
          {isEN ? 'Enter a valid date and time to see the conversion.' : 'Informe uma data e hora válidas para ver a conversão.'}
        </div>
      )}
    </div>
  );
}

export default TimezoneConverter;
